import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import wordmarkRed from "../assets/logo-wordmark-red.webp";

const NAV = [
  { to: "/experience", label: "Experience" },
  { to: "/solutions", label: "Solutions" },
  { to: "/intelligence", label: "Business Intelligence" },
  { to: "/trust", label: "Trust & Security" },
  { to: "/affiliate", label: "Affiliates" },
  { to: "/blog", label: "Blog" },
  { to: "/login", label: "Partner Login" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const barClass = scrolled
    ? "bg-tk-ink/78 backdrop-blur-xl border-b border-tk-line"
    : "bg-transparent";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${barClass}`}
    >
      <div className="mx-auto flex max-w-content items-center justify-between px-gutter py-4">
        <Link to="/" aria-label="thatKorner — home" className="flex items-center">
          <img
            src={wordmarkRed}
            alt="thatKorner"
            width={1600}
            height={542}
            fetchPriority="high"
            decoding="async"
            className="h-10 w-auto drop-shadow-[0_4px_18px_rgba(240,31,14,0.45)] md:h-12 lg:h-14"
          />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative font-display text-[13.5px] font-light tracking-tight transition-colors ${
                  active ? "text-tk-cream" : "text-tk-cream/70 hover:text-tk-cream"
                } after:absolute after:inset-x-0 after:-bottom-1.5 after:h-px after:origin-left after:scale-x-0 after:bg-tk-red after:transition-transform after:duration-300 hover:after:scale-x-100`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/demo"
            className="group inline-flex items-center gap-2.5 rounded-full border border-tk-line bg-tk-char/80 px-4 py-2 font-display text-[12.5px] font-medium tracking-tight text-tk-cream transition-colors hover:border-tk-red/60 hover:bg-tk-red/10"
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 animate-ping rounded-full bg-tk-red/70" />
              <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-tk-red shadow-[0_0_10px_rgba(240,31,14,0.7)]" />
            </span>
            Book a demo
          </Link>
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center text-tk-cream lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-tk-line bg-tk-ink lg:hidden">
          <nav className="flex flex-col px-gutter py-4">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="border-b border-tk-line py-3.5 font-display text-[15px] font-light text-tk-cream/85"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/demo"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-tk-red px-5 py-3 font-display text-[12.5px] font-semibold uppercase tracking-[0.18em] text-tk-cream"
            >
              Book a demo
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
